import { useState, useRef, useEffect, useCallback } from 'react';

import { Label, Textarea, cn } from '@parrot-software/design-system';
import { Search } from '@parrot-software/design-system/icons';

import type { PlaceAddressDetailsDto, PlacePredictionDto } from '@onboarding/application/dtos';

import { SuggestionsDropdown } from './SuggestionsDropdown';

type AddressSearchInputProps = {
  /** Whether the component is disabled */
  disabled?: boolean;
  /** Error message to display */
  error?: string;
  /** Whether predictions are loading */
  isLoading?: boolean;
  /** Label text for the input */
  label?: string;
  /** Callback when the user selects an address */
  onSelect: (address: PlaceAddressDetailsDto) => void;
  /** Placeholder text */
  placeholder?: string;
  /** List of autocomplete predictions */
  predictions: PlacePredictionDto[];
  /** Whether the field is required */
  required?: boolean;
  /** Function to select a place by placeId */
  selectPlace: (placeId: string) => Promise<PlaceAddressDetailsDto>;
  /** Current search query value */
  value: string;
  /** Update the search query */
  onChange: (value: string) => void;
};

/**
 * Address search input with autocomplete dropdown.
 * Integrates with Google Places API through the onboarding composition layer.
 */
export const AddressSearchInput = ({
  disabled = false,
  error,
  isLoading = false,
  label,
  onSelect,
  placeholder = 'Buscar direccion...',
  predictions,
  required,
  selectPlace,
  value,
  onChange,
}: AddressSearchInputProps) => {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const anchorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setActiveIndex(-1);
  }, [predictions]);

  const handleSelectPlace = useCallback(
    async (placeId: string) => {
      setShowSuggestions(false);
      const address = await selectPlace(placeId);
      onSelect(address);
    },
    [selectPlace, onSelect],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!showSuggestions || predictions.length === 0) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setActiveIndex((prev) => (prev < predictions.length - 1 ? prev + 1 : 0));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setActiveIndex((prev) => (prev > 0 ? prev - 1 : predictions.length - 1));
          break;
        case 'Enter':
          e.preventDefault();
          if (activeIndex >= 0 && predictions[activeIndex]) {
            handleSelectPlace(predictions[activeIndex].placeId);
          }
          break;
        case 'Escape':
          e.preventDefault();
          setShowSuggestions(false);
          setActiveIndex(-1);
          break;
      }
    },
    [showSuggestions, predictions, activeIndex, handleSelectPlace],
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onChange(e.target.value);
    setShowSuggestions(true);
  };

  const hasResults = predictions.length > 0;

  return (
    <div className="flex flex-col gap-2">
      {label && (
        <Label className="text-sm font-semibold text-foreground">
          {label}
          {required && <span className="text-destructive"> *</span>}
        </Label>
      )}
      <div className="relative">
        <div ref={anchorRef}>
          <Textarea
            autoComplete="off"
            autoGrow
            className={cn({ 'border-destructive': error })}
            disabled={disabled}
            leftIcon={<Search size={16} />}
            onBlur={() => setShowSuggestions(false)}
            onChange={handleInputChange}
            onFocus={() => setShowSuggestions(true)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            value={value}
          />
          {isLoading && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-muted-foreground border-t-transparent" />
            </div>
          )}
        </div>

        {showSuggestions && hasResults && (
          <SuggestionsDropdown
            activeIndex={activeIndex}
            anchorRef={anchorRef}
            onActiveIndexChange={setActiveIndex}
            onSelect={handleSelectPlace}
            predictions={predictions}
          />
        )}
      </div>
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
};
